import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Minus, Plus, Truck, RefreshCw, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Layout } from '../components/Layout';
import { getProduct, getProducts } from '../services/api';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import { toast } from 'sonner';

// Product-specific reviews
const productReviews = {
  'cashmere-capes-ponchos': [
    { name: 'Sophie M.', rating: 5, date: '2 weeks ago', review: 'Absolutely stunning cape! The cashmere is incredibly soft and the craftsmanship is impeccable. Perfect for elegant evenings out.', verified: true },
    { name: 'Elena R.', rating: 5, date: '1 month ago', review: 'This cape exceeded my expectations. The quality is outstanding and it keeps me warm without being bulky. Worth every penny!', verified: true },
    { name: 'Charlotte B.', rating: 5, date: '1 month ago', review: 'I receive compliments every time I wear this beautiful piece. The color is exactly as shown and the material is luxurious.', verified: true },
    { name: 'Amelia K.', rating: 4, date: '2 months ago', review: 'Gorgeous poncho with beautiful draping. The only reason for 4 stars is I wish it came in more colors. Will definitely buy again!', verified: true },
    { name: 'Isabella F.', rating: 5, date: '2 months ago', review: 'A timeless piece that elevates any outfit. The warmth and softness of the cashmere is unmatched. Highly recommend!', verified: true },
    { name: 'Olivia H.', rating: 5, date: '3 months ago', review: 'Perfect for travel - lightweight yet warm. The quality speaks for itself. This is true luxury.', verified: true },
    { name: 'Emma L.', rating: 5, date: '3 months ago', review: 'I was hesitant about the price but this cape is worth it. The attention to detail is remarkable.', verified: true },
  ],
  'cashmere-pullovers': [
    { name: 'James W.', rating: 5, date: '1 week ago', review: 'The softest pullover I have ever owned. Fits perfectly and the quality is exceptional. A wardrobe staple now.', verified: true },
    { name: 'Michael T.', rating: 5, date: '3 weeks ago', review: 'Incredible quality cashmere. Lightweight but very warm. The fit is true to size and very flattering.', verified: true },
    { name: 'Sarah P.', rating: 5, date: '1 month ago', review: 'Bought this for my husband and he absolutely loves it. The material is so soft and comfortable.', verified: true },
    { name: 'David K.', rating: 4, date: '1 month ago', review: 'Beautiful pullover with great construction. Keeps its shape after washing. Minor pilling after several wears but expected with cashmere.', verified: true },
    { name: 'Anna M.', rating: 5, date: '2 months ago', review: 'This pullover is perfection! The color is rich and the cashmere quality is evident. Fast shipping too!', verified: true },
    { name: 'Robert J.', rating: 5, date: '2 months ago', review: 'Worth the investment. This is real luxury - you can feel the difference from department store cashmere.', verified: true },
    { name: 'Lisa C.', rating: 5, date: '3 months ago', review: 'My third purchase from this brand. The quality is consistently excellent. This pullover is heavenly soft.', verified: true },
  ],
  'cashmere-caps': [
    { name: 'Hannah S.', rating: 5, date: '1 week ago', review: 'Such a chic beanie! Keeps my head warm without flattening my hair. The cashmere is divine.', verified: true },
    { name: 'Thomas R.', rating: 5, date: '2 weeks ago', review: 'Perfect fit and incredibly soft. This cap is both stylish and functional. Great for cold mornings.', verified: true },
    { name: 'Grace L.', rating: 5, date: '1 month ago', review: 'Love this cap! It is elegant enough for a night out but cozy for everyday wear. Excellent quality.', verified: true },
    { name: 'William B.', rating: 4, date: '1 month ago', review: 'Very nice cap, soft and warm. Wish it had more color options but the quality is undeniable.', verified: true },
    { name: 'Victoria N.', rating: 5, date: '2 months ago', review: 'The most comfortable beanie I have owned. Does not itch at all and looks very sophisticated.', verified: true },
    { name: 'Daniel M.', rating: 5, date: '2 months ago', review: 'Bought as a gift and they absolutely loved it. The presentation was beautiful too. Highly recommend!', verified: true },
    { name: 'Catherine A.', rating: 5, date: '3 months ago', review: 'Exceptional quality and craftsmanship. This cap has become my go-to accessory for winter.', verified: true },
  ],
  'tribal-ponchos': [
    { name: 'Maya P.', rating: 5, date: '1 week ago', review: 'Absolutely love the authentic tribal patterns! This poncho is a conversation starter everywhere I go.', verified: true },
    { name: 'Lucas H.', rating: 5, date: '2 weeks ago', review: 'The craftsmanship is amazing. You can tell this was made with care and tradition. Very warm too!', verified: true },
    { name: 'Zoe K.', rating: 5, date: '1 month ago', review: 'Such a unique piece! The wool quality is excellent and the design is beautiful. Perfect for festivals.', verified: true },
    { name: 'Noah G.', rating: 4, date: '1 month ago', review: 'Beautiful poncho with great detailing. A bit heavier than expected but that means it is very warm!', verified: true },
    { name: 'Aria S.', rating: 5, date: '2 months ago', review: 'This poncho brings me so much joy. The colors are vibrant and the pattern is stunning. A treasure!', verified: true },
    { name: 'Ethan W.', rating: 5, date: '2 months ago', review: 'Wearing a piece of art. The tribal design is authentic and the quality is superb. Love supporting traditional crafts.', verified: true },
    { name: 'Luna R.', rating: 5, date: '3 months ago', review: 'Perfect for yoga retreats and outdoor gatherings. Comfortable, warm, and absolutely gorgeous!', verified: true },
  ],
  'tribal-shorts': [
    { name: 'Kai M.', rating: 5, date: '1 week ago', review: 'Most comfortable yoga pants ever! The fabric moves with you and the pattern is beautiful.', verified: true },
    { name: 'River J.', rating: 5, date: '2 weeks ago', review: 'Perfect for my meditation practice. Soft, breathable, and the design adds a spiritual touch.', verified: true },
    { name: 'Sage T.', rating: 5, date: '1 month ago', review: 'Love these shorts! Great for hot yoga and lounging at home. The material is high quality.', verified: true },
    { name: 'Phoenix L.', rating: 4, date: '1 month ago', review: 'Really nice yoga pants. Comfortable fit and love the tribal design. Runs slightly large so size down.', verified: true },
    { name: 'Willow C.', rating: 5, date: '2 months ago', review: 'These have become my favorite pants for both yoga and casual wear. So comfortable and stylish!', verified: true },
    { name: 'Ocean B.', rating: 5, date: '2 months ago', review: 'The sacred geometry print is stunning. Fabric is soft and durable. Perfect for my spiritual practice.', verified: true },
    { name: 'Sky H.', rating: 5, date: '3 months ago', review: 'Exactly what I was looking for. Authentic design, comfortable fit, and excellent quality. Namaste!', verified: true },
  ],
};

// Get reviews for a product based on its category
const getProductReviews = (category) => {
  return productReviews[category] || productReviews['cashmere-pullovers'];
};

// Star rating component
const StarRating = ({ rating }) => {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${star <= rating ? 'fill-[#C6A87C] text-[#C6A87C]' : 'fill-[#EBE5DE] text-[#EBE5DE]'}`}
        />
      ))}
    </div>
  );
};

export const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isZoomed, setIsZoomed] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);
      try {
        const productData = await getProduct(id);
        setProduct(productData);
        
        // Set defaults
        if (productData.sizes.length > 0) {
          setSelectedSize(productData.sizes[0]);
        }
        if (productData.colors.length > 0) {
          setSelectedColor(productData.colors[0]);
          // If color_images mapping exists, show the first color's image
          if (productData.color_images && productData.color_images[productData.colors[0]]) {
            const colorImageIndex = productData.images.indexOf(productData.color_images[productData.colors[0]]);
            if (colorImageIndex !== -1) {
              setSelectedImageIndex(colorImageIndex);
            }
          }
        }
        
        // Load related products
        const allProducts = await getProducts(productData.category);
        setRelatedProducts(allProducts.filter(p => p.id !== id).slice(0, 3));
      } catch (error) {
        console.error('Failed to load product:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
    window.scrollTo(0, 0);
  }, [id]);

  // Handle color selection - update image if mapped
  const handleColorSelect = (color) => {
    setSelectedColor(color);
    if (product.color_images && product.color_images[color]) {
      const colorImageIndex = product.images.indexOf(product.color_images[color]);
      if (colorImageIndex !== -1) {
        setSelectedImageIndex(colorImageIndex);
      }
    }
  };

  const handleAddToCart = () => {
    if (product.sizes.length > 0 && !selectedSize) {
      toast.error('Please select a size');
      return;
    }
    if (product.colors.length > 0 && !selectedColor) {
      toast.error('Please select a color');
      return;
    }
    
    addItem(product, quantity, selectedSize, selectedColor);
    toast.success(`${product.name} added to cart`, {
      description: `${quantity} x €${product.price.toFixed(2)}`,
    });
  };

  const nextImage = () => {
    if (product && product.images.length > 1) {
      setSelectedImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product && product.images.length > 1) {
      setSelectedImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-spacing flex justify-center items-center min-h-[50vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="section-spacing container-luxury text-center">
          <h1 className="font-heading text-3xl text-[#2C2420] mb-4">Product Not Found</h1>
          <Link to="/shop">
            <Button className="btn-primary">Back to Shop</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const currentImage = product.images[selectedImageIndex] || product.images[0];

  return (
    <Layout>
      {/* Breadcrumb */}
      <div className="container-luxury py-6">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-sm text-[#8C8580] hover:text-[#2C2420] transition-colors"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Shop
        </Link>
      </div>

      {/* Product Details */}
      <section className="container-luxury pb-16" data-testid="product-detail">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* Main Image */}
            <div
              className={`aspect-product bg-[#F2EFE9] overflow-hidden relative cursor-zoom-in ${
                isZoomed ? 'cursor-zoom-out' : ''
              }`}
              onClick={() => setIsZoomed(!isZoomed)}
              data-testid="product-image-container"
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={selectedImageIndex}
                  src={currentImage}
                  alt={product.name}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`w-full h-full object-cover transition-transform duration-500 ${
                    isZoomed ? 'scale-150' : ''
                  }`}
                  data-testid="product-main-image"
                />
              </AnimatePresence>
              
              {/* Navigation Arrows */}
              {product.images.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="h-5 w-5 text-[#2C2420]" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="h-5 w-5 text-[#2C2420]" />
                  </button>
                </>
              )}
              
              {/* Image Counter */}
              {product.images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 rounded-full text-white text-xs">
                  {selectedImageIndex + 1} / {product.images.length}
                </div>
              )}
            </div>
            
            {product.is_featured && (
              <span className="absolute top-4 left-4 px-4 py-2 bg-[#C6A87C] text-[#2C2420] text-xs uppercase tracking-wider">
                Featured
              </span>
            )}

            {/* Thumbnail Gallery */}
            {product.images.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-24 bg-[#F2EFE9] overflow-hidden border-2 transition-all ${
                      selectedImageIndex === index
                        ? 'border-[#2C2420]'
                        : 'border-transparent hover:border-[#C6A87C]'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} view ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col"
          >
            <p className="text-xs uppercase tracking-widest text-[#C6A87C] mb-2">
              {product.category.replace(/-/g, ' ')}
            </p>
            <h1
              className="font-heading text-3xl md:text-4xl text-[#2C2420] mb-4"
              data-testid="product-title"
            >
              {product.name}
            </h1>
            <p
              className="text-2xl text-[#2C2420] font-body mb-6"
              data-testid="product-price"
            >
              €{product.price.toFixed(2)}
            </p>

            <p className="text-base text-[#5D5550] leading-relaxed mb-8">
              {product.description}
            </p>

            {/* Color Selection */}
            {product.colors.length > 0 && (
              <div className="mb-6">
                <p className="text-xs uppercase tracking-widest text-[#8C8580] mb-3">
                  Color: <span className="text-[#2C2420]">{selectedColor}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorSelect(color)}
                      className={`px-4 py-2 text-sm border transition-colors ${
                        selectedColor === color
                          ? 'border-[#2C2420] bg-[#2C2420] text-[#F9F8F6]'
                          : 'border-[#EBE5DE] text-[#5D5550] hover:border-[#2C2420]'
                      }`}
                      data-testid={`color-${color}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection */}
            {product.sizes.length > 0 && (
              <div className="mb-6">
                <p className="text-xs uppercase tracking-widest text-[#8C8580] mb-3">
                  Size: <span className="text-[#2C2420]">{selectedSize}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`w-12 h-12 text-sm border transition-colors ${
                        selectedSize === size
                          ? 'border-[#2C2420] bg-[#2C2420] text-[#F9F8F6]'
                          : 'border-[#EBE5DE] text-[#5D5550] hover:border-[#2C2420]'
                      }`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex items-center border border-[#EBE5DE]">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-3 hover:bg-[#F2EFE9] transition-colors"
                  data-testid="quantity-decrease"
                >
                  <Minus className="h-4 w-4 text-[#5D5550]" />
                </button>
                <span
                  className="w-12 text-center text-[#2C2420]"
                  data-testid="quantity-value"
                >
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-3 hover:bg-[#F2EFE9] transition-colors"
                  data-testid="quantity-increase"
                >
                  <Plus className="h-4 w-4 text-[#5D5550]" />
                </button>
              </div>

              <Button
                className="btn-primary flex-1"
                onClick={handleAddToCart}
                data-testid="add-to-cart-btn"
              >
                Add to Cart — €{(product.price * quantity).toFixed(2)}
              </Button>
            </div>

            {/* Benefits */}
            <div className="border-t border-[#EBE5DE] pt-6 space-y-4">
              <div className="flex items-center gap-3 text-sm text-[#5D5550]">
                <Truck className="h-5 w-5 text-[#C6A87C]" />
                <span>Free worldwide shipping on orders above €150</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#5D5550]">
                <RefreshCw className="h-5 w-5 text-[#C6A87C]" />
                <span>Free 30-day returns</span>
              </div>
            </div>

            {/* Product Details Accordion */}
            <div className="border-t border-[#EBE5DE] mt-6 pt-6 space-y-4">
              {product.fabric_details && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#8C8580] mb-2">
                    Fabric Details
                  </p>
                  <p className="text-sm text-[#5D5550]">{product.fabric_details}</p>
                </div>
              )}
              {product.care_instructions && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#8C8580] mb-2">
                    Care Instructions
                  </p>
                  <p className="text-sm text-[#5D5550]">{product.care_instructions}</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="section-spacing border-t border-[#EBE5DE]" data-testid="reviews-section">
        <div className="container-luxury">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-[#2C2420] mb-2">
                Customer Reviews
              </h2>
              <div className="flex items-center gap-3">
                <StarRating rating={5} />
                <span className="text-sm text-[#5D5550]">
                  4.9 out of 5 based on {getProductReviews(product.category).length} reviews
                </span>
              </div>
            </div>
          </div>

          <div className="grid gap-6">
            {getProductReviews(product.category).map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#F9F8F6] p-6 border border-[#EBE5DE]"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-[#2C2420]">{review.name}</span>
                      {review.verified && (
                        <span className="text-xs bg-[#C6A87C]/20 text-[#8B7355] px-2 py-0.5 rounded">
                          Verified Purchase
                        </span>
                      )}
                    </div>
                    <StarRating rating={review.rating} />
                  </div>
                  <span className="text-xs text-[#8C8580]">{review.date}</span>
                </div>
                <p className="text-[#5D5550] leading-relaxed">{review.review}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="section-spacing bg-[#F2EFE9]" data-testid="related-products">
          <div className="container-luxury">
            <h2 className="font-heading text-2xl md:text-3xl text-[#2C2420] mb-12 text-center">
              You May Also Like
            </h2>
            <div className="product-grid">
              {relatedProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};
